// canonical-client-redirect-v2 — resolve the canonical target for a fetched
// CRM contact/organization record so a merged-away (or since-removed) id never
// dead-ends on the record page.
//
// CONTEXT (MC-27052 contact-404 investigation, 2026-07-05): the record loader
// `fetchClient(id)` → `GET /api/crm/clients/:id` is the ONLY correct bare-record
// endpoint. MC-27052 itself is healthy and loads 200. The general robustness
// gap this guards: a MERGED loser id (or a since-removed loser no longer in the
// `clients` view) should transparently land on its SURVIVING canonical record
// instead of hard-404ing. Two signals can carry the survivor:
//   1. `merged_into` — the dedup tombstone pointer stamped on a merged loser row
//      that is STILL present in the `clients` view (existing behavior).
//   2. `redirected` + `requested_id` — the server, recovering a survivor for a
//      loser id GONE from the `clients` view, returns THE SURVIVOR ROW and echoes
//      the originally-requested (loser) id as `requested_id`.
//
// v1 → v2 FIX (the tester-confirmed contract gap): the server always returns the
// *survivor* row for the redirect path, so that row's own `unified_client_id`
// EQUALS `canonical_id`. v1 decided the redirect by comparing `canonical_id`
// against the returned row's own id — identical for this path — so the
// self-reference guard ALWAYS suppressed the redirect: the URL never rewrote and
// the merged-record toast never fired. The v1 unit test only passed because it
// fed an input shape (`unified_client_id` ≠ `canonical_id`) the backend can never
// produce. v2 keys the decision off the echoed `requested_id` (the loser id the
// URL still points at) and redirects when it differs from the survivor/canonical
// id. A legacy shape where `canonical_id` differs from the returned row's id
// still redirects (back-compat), and the `merged_into` tombstone path is
// unchanged.
//
// This module is a PURE helper (regression-guarded by canonicalClient.test.ts)
// so the redirect-decision precedence + self-reference guard are verified in CI.
// See docs/REGRESSIONS.md.

/** Minification-proof deploy marker for this fix — emitted as the
 *  `X-Canonical-Redirect` response header on a redirected
 *  `GET /api/crm/clients/:id` AND referenced in the frontend redirect path, so a
 *  deploy is live-verifiable in both the worker and SPA bundles (the string
 *  literal survives bundling/minification). */
export const CANONICAL_CLIENT_MARKER = 'canonical-client-redirect-v2'

/** Minimal shape of the fields that drive the canonical-target decision. The
 *  real fetched record carries far more; only these matter here. */
export interface CanonicalClientLike {
  unified_client_id?: string | null
  client_type?: string | null
  /** Dedup tombstone survivor pointer (present on a merged loser still in-view). */
  merged_into?: string | null
  /** Server flag: the returned record is the SURVIVOR returned in place of a
   *  requested id that resolved through a merge to a different record. */
  redirected?: boolean | null
  /** Server-resolved canonical id. On the server-redirect path this equals the
   *  returned survivor row's own `unified_client_id`; it may differ from it only
   *  in a legacy/alternate shape. */
  canonical_id?: string | null
  /** Server echo of the ORIGINALLY-requested id (the merged-away loser the URL
   *  still points at). This is the signal that makes the server-redirect path
   *  detectable — the returned row IS the survivor, so its own id can't reveal
   *  the loser we navigated from. */
  requested_id?: string | null
}

export interface CanonicalClientTarget {
  /** True when the caller should navigate to `canonicalId` instead of rendering
   *  this record in place. */
  redirect: boolean
  /** The surviving canonical id to navigate to (only meaningful when
   *  `redirect` is true). */
  canonicalId: string | null
  /** Whether the record is an organization (drives the `/organizations` vs
   *  `/contacts` route segment at the call site). */
  isOrg: boolean
}

function blankish(v: unknown): boolean {
  return v === null || v === undefined || (typeof v === 'string' && v.trim() === '')
}

/**
 * Decide whether a fetched client record should redirect to a surviving
 * canonical record, and to which id.
 *
 * Precedence: an explicit server-resolved redirect (`redirected` + a
 * `requested_id` that differs from the returned survivor) wins over the
 * `merged_into` tombstone pointer (the server already followed the full merge
 * chain), then `merged_into` as the fallback. On the server-redirect path the
 * survivor's own id EQUALS `canonical_id`, so the decision keys off
 * `requested_id` (the loser id the URL points at) — NOT a self-comparison of
 * `canonical_id`, which would always match and suppress the redirect. A blank
 * target, or a requested id already equal to the canonical id, never redirects.
 */
export function resolveCanonicalClientTarget(
  c: CanonicalClientLike | null | undefined,
): CanonicalClientTarget {
  const isOrg = String(c?.client_type ?? '').toLowerCase() === 'organization'
  if (!c) return { redirect: false, canonicalId: null, isOrg }

  const selfId = blankish(c.unified_client_id) ? '' : String(c.unified_client_id)

  // Server-resolved redirect (canonical-client-redirect-v2). The returned row IS
  // the survivor, so `canonical_id` (when present) == its own id; the loser we
  // navigated from is carried in `requested_id`.
  if (c.redirected) {
    const canonicalTarget = blankish(c.canonical_id) ? selfId : String(c.canonical_id)
    const requested = blankish(c.requested_id) ? '' : String(c.requested_id)
    if (canonicalTarget) {
      if (requested) {
        // Real backend-producible contract: survivor row + echoed loser id.
        if (requested !== canonicalTarget) {
          return { redirect: true, canonicalId: canonicalTarget, isOrg }
        }
      } else if (canonicalTarget !== selfId) {
        // Legacy/alternate shape: canonical_id differs from the returned row's id.
        return { redirect: true, canonicalId: canonicalTarget, isOrg }
      }
    }
  }

  // Tombstone fallback: a merged loser still present in-view carries a
  // `merged_into` pointer to its survivor (and is NOT flagged `redirected`).
  const tombstoneTarget = blankish(c.merged_into) ? '' : String(c.merged_into)
  if (tombstoneTarget && tombstoneTarget !== selfId) {
    return { redirect: true, canonicalId: tombstoneTarget, isOrg }
  }

  return { redirect: false, canonicalId: null, isOrg }
}
